/**
 * useUserLookup — resolve audit-actor user ids (createdBy / updatedBy) to display
 * names. Audit fields are user_master ids, so these use `userLookupService`
 * (NOT the personnel lookup). Unknown ids resolve to `-`, never a raw ObjectId.
 */

import { useEffect, useState } from 'react';
import { userLookupService } from '../services/user-lookup.service';

/** createdBy / updatedBy -> names. Convenient for view / detail pages. */
export function useUserAuditNames(
  createdBy: string | null | undefined,
  updatedBy: string | null | undefined
): { createdByName: string; updatedByName: string; loading: boolean } {
  const [createdByName, setCreatedByName] = useState<string>('-');
  const [updatedByName, setUpdatedByName] = useState<string>('-');
  const [loading, setLoading] = useState<boolean>(!!createdBy || !!updatedBy);

  useEffect(() => {
    const ids = [createdBy, updatedBy].filter((id): id is string => !!id);
    if (ids.length === 0) {
      setCreatedByName('-');
      setUpdatedByName('-');
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    userLookupService.getNamesByIds(ids).then((map) => {
      if (mounted) {
        setCreatedByName(createdBy ? map.get(createdBy) || '-' : '-');
        setUpdatedByName(updatedBy ? map.get(updatedBy) || '-' : '-');
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [createdBy, updatedBy]);

  return { createdByName, updatedByName, loading };
}

/**
 * Resolve an arbitrary set of actor ids to display names — SME lists, reviewer
 * lists, anywhere a stored user_master id has to be shown to a person.
 *
 * Returns a lookup rather than an array so the caller keeps its own ordering.
 * Unknown ids resolve to `-`, never a raw ObjectId: an id on screen is noise to
 * everyone except whoever is debugging.
 */
export function useUserNames(
  ids: (string | null | undefined)[]
): { nameOf: (id: string | null | undefined) => string; loading: boolean } {
  const [map, setMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);

  // A fresh array every render would re-run the effect forever, so depend on the
  // ids themselves rather than the array identity.
  const key = ids.filter((id): id is string => !!id).sort().join(',');

  useEffect(() => {
    if (!key) {
      setMap(new Map());
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    userLookupService.getNamesByIds(key.split(',')).then((resolved) => {
      if (!mounted) return;
      setMap(resolved);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [key]);

  return {
    nameOf: (id) => (id ? map.get(id) || '-' : '-'),
    loading,
  };
}

export default useUserAuditNames;
